import { AcademyApiError } from "../../../../shared/academyApi";
import { IdentitySecurityControls } from "../../../../shared/IdentitySecurityControls";
import { identitySecurity } from "../../../../shared/identityClient";
import { useIdentity } from "../../../../shared/useIdentity";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ICON_PATHS } from "../../config/icons";
import { APP_DATA } from "../../data/mockData";
import type {
  Accommodation,
  Candidate,
  EvidenceRecord,
  Incident,
  ModalKind,
  ProctorActionSpec,
  ProctorMetricSpec,
  ProctorNoteSpec,
  ProctorWorkflowSpec,
  ProctorWorkspaceSpec,
  Session,
  Toast,
  WorkspaceRow,
} from "../../types/domain";
import { downloadText, safeRead, safeWrite } from "../../utils/browser";

export function Icon({
  name,
  size = 18,
  className = "",
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const d = ICON_PATHS[name] || ICON_PATHS.grid;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .map(v => v[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
export const statusTone = (value: string) => {
  const v = value.toLowerCase();
  if (
    v.includes("urgent") ||
    v.includes("protected") ||
    v.includes("escalated") ||
    v.includes("failed") ||
    v.includes("blocked")
  )
    return "danger";
  if (
    v.includes("review") ||
    v.includes("fair") ||
    v.includes("re-check") ||
    v.includes("pause") ||
    v.includes("waiting") ||
    v.includes("preflight")
  )
    return "warn";
  if (
    v.includes("ready") ||
    v.includes("normal") ||
    v.includes("verified") ||
    v.includes("resolved") ||
    v.includes("sealed") ||
    v.includes("connected") ||
    v.includes("healthy") ||
    v.includes("live")
  )
    return "good";
  return "info";
};
export function Status({ children }: { children: React.ReactNode }) {
  return <span className={`status status-${statusTone(String(children))}`}>{children}</span>;
}
export function Progress({ value, green = false }: { value: number; green?: boolean }) {
  const normalizedValue = Math.max(0, Math.min(100, value));

  return (
    <div
      className={`progress ${green ? "progress-green" : ""}`}
      role="progressbar"
      aria-label={`${normalizedValue}% complete`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={normalizedValue}
    >
      <span style={{ width: `${normalizedValue}%` }} />
    </div>
  );
}
export function PageHero({
  kicker,
  title,
  description,
  children,
}: {
  kicker: string;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-hero">
      <div className="page-hero-copy">
        <div className="page-kicker">{kicker}</div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {children && <div className="hero-actions">{children}</div>}
    </div>
  );
}
export function Card({
  title,
  subtitle,
  actions,
  children,
  className = "",
}: {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-header">
          <div className="card-title">
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
export function Metric({
  icon,
  value,
  label,
  change,
  color = "rgba(139,92,246,.18)",
  text = "#c9b6ff",
}: {
  icon: string;
  value: string;
  label: string;
  change?: string;
  color?: string;
  text?: string;
}) {
  return (
    <div
      className="metric-card"
      style={{ "--metric-color": color, "--metric-text": text } as React.CSSProperties}
    >
      <div className="metric-head">
        <div className="metric-icon">
          <Icon name={icon} size={17} />
        </div>
        {change && <span className="metric-change">{change}</span>}
      </div>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
    </div>
  );
}
export function Button({
  children,
  onClick,
  icon,
  variant = "",
  small = false,
  type = "button",
  disabled = false,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  icon?: string;
  variant?: string;
  small?: boolean;
  type?: "button" | "submit";
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type={type}
      className={`btn ${variant ? `btn-${variant}` : ""} ${small ? "btn-sm" : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {icon && <Icon name={icon} size={small ? 14 : 16} />}
      <span>{children}</span>
    </button>
  );
}
export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: string;
  title: string;
  text: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="proctor-empty">
      <span>
        <Icon name={icon} size={23} />
      </span>
      <b>{title}</b>
      <p>{text}</p>
      {action}
    </div>
  );
}
export function PlatformChip({ platform }: { platform: string }) {
  const cls = platform.includes("Labs") ? "labs" : platform.includes("Class") ? "class" : "academy";
  return (
    <span className={`platform-chip ${cls}`}>
      <Icon name={cls === "labs" ? "flask" : cls === "class" ? "video" : "shield"} size={12} />
      {platform}
    </span>
  );
}
export function Toggle({ value, onChange, label }: { value: boolean; onChange: () => void; label: string }) {
  return (
    <button
      type="button"
      className={`toggle ${value ? "on" : ""}`}
      onClick={onChange}
      aria-pressed={value}
      aria-label={label}
    >
      <span />
    </button>
  );
}

export type AppCallbacks = {
  navigate: (area: string, page: string) => void;
  toast: (title: string, message: string, tone?: Toast["tone"]) => void;
  openModal: (kind: ModalKind, payload?: unknown) => void;
  openClass: (session: Session, mode?: string) => void;
  openLab: (session: Session, destination?: string) => void;
  openExternal: (url: string, label: string) => void;
  classSessions: Session[];
  classSessionsLoading: boolean;
  classSessionsError: string;
};

type WorkspacePageProps = { cb: AppCallbacks; spec: ProctorWorkspaceSpec };
type WorkspaceLayoutProps = {
  cb: AppCallbacks;
  spec: ProctorWorkspaceSpec;
  rows: WorkspaceRow[];
  filtered: WorkspaceRow[];
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  statusFilter: string;
  setStatusFilter: React.Dispatch<React.SetStateAction<string>>;
  openRow: (row: WorkspaceRow) => void;
  advanceRow: (row: WorkspaceRow) => void;
  execute: (action: ProctorActionSpec, row?: WorkspaceRow) => void;
  checks: Record<string, boolean>;
  toggleCheck: (key: string) => void;
  formValues: Record<string, string>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  saveForm: () => void;
};

function sourceRows(spec: ProctorWorkspaceSpec): WorkspaceRow[] {
  if (spec.source === "sessions") return APP_DATA.sessions as unknown as WorkspaceRow[];
  if (spec.source === "candidates") return APP_DATA.candidates as unknown as WorkspaceRow[];
  if (spec.source === "incidents") return APP_DATA.incidents as unknown as WorkspaceRow[];
  if (spec.source === "evidence") return APP_DATA.evidence as unknown as WorkspaceRow[];
  if (spec.source === "accommodations") return APP_DATA.accommodations as unknown as WorkspaceRow[];
  return spec.rows || [];
}
function rowValue(row: WorkspaceRow, key: string) {
  const value = row[key];
  return typeof value === "boolean" ? (value ? "Yes" : "No") : String(value ?? "—");
}
function rowTitle(row: WorkspaceRow) {
  return String(
    row.name ?? row.title ?? row.summary ?? row.type ?? row.id ?? row.candidate ?? "Operational record",
  );
}
function rowDetail(row: WorkspaceRow) {
  return String(
    row.detail ?? row.description ?? row.facts ?? row.program ?? row.instructions ?? row.source ?? "",
  );
}
function rowStatus(row: WorkspaceRow, spec: ProctorWorkspaceSpec) {
  return String(row[spec.statusKey] ?? row.status ?? row.state ?? row.severity ?? "Ready");
}
function rowOwner(row: WorkspaceRow) {
  return String(row.owner ?? row.lead ?? row.custodian ?? "Assigned operator");
}
function metricPalette(tone: string) {
  if (tone === "good") return { color: "rgba(26,211,123,.15)", text: "#73e4ad" };
  if (tone === "warn") return { color: "rgba(247,127,0,.17)", text: "#ffba67" };
  if (tone === "danger") return { color: "rgba(251,113,133,.16)", text: "#ff9bac" };
  if (tone === "violet") return { color: "rgba(139,92,246,.18)", text: "#c9b6ff" };
  return { color: "rgba(47,214,244,.15)", text: "#78e9fa" };
}
export function WorkspaceRouteBoundary({
  spec,
  children,
}: {
  spec: ProctorWorkspaceSpec;
  children: React.ReactNode;
}) {
  return (
    <div
      className="proctor-route-marker"
      data-route-id={spec.id}
      data-layout={spec.family}
      data-model={spec.model}
      data-signature={spec.signature}
    >
      {children}
    </div>
  );
}
function WorkspaceMetrics({ metrics }: { metrics: ProctorMetricSpec[] }) {
  return (
    <div className="metrics-grid">
      {metrics.map(item => {
        const palette = metricPalette(item.tone);
        return (
          <Metric
            key={item.label}
            icon={item.icon}
            value={item.value}
            label={item.label}
            change={item.trend}
            color={palette.color}
            text={palette.text}
          />
        );
      })}
    </div>
  );
}
function WorkspaceTabs({
  tabs,
  active,
  setActive,
}: {
  tabs: string[];
  active: string;
  setActive: (value: string) => void;
}) {
  return (
    <div className="v2-tabs" role="tablist" aria-label="Workspace views">
      {tabs.map(tab => (
        <button
          type="button"
          role="tab"
          aria-selected={active === tab}
          className={active === tab ? "active" : ""}
          key={tab}
          onClick={() => setActive(tab)}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}
function WorkspaceTabPanel({
  title,
  activeTab,
  recordCount,
}: {
  title: string;
  activeTab: string;
  recordCount: number;
}) {
  const normalized = activeTab.toLowerCase();
  const focus = normalized.includes("context")
    ? "Scope, purpose and the current operational context are in focus."
    : normalized.includes("relationship")
      ? "Linked people, permissions and responsibility boundaries are in focus."
      : normalized.includes("sponsor")
        ? "Funding, sponsor visibility and support relationships are in focus."
        : normalized.includes("transition") || normalized.includes("handoff")
          ? "Ownership changes, handoffs and the next safe action are in focus."
          : normalized.includes("timeline")
            ? "Recent activity and upcoming actions are in focus."
            : `The ${activeTab.toLowerCase()} view is in focus for this workspace.`;
  return (
    <section className="workspace-tab-panel" role="tabpanel" aria-label={`${activeTab} view`}>
      <div className="workspace-tab-panel-icon">
        <Icon name="grid" size={18} />
      </div>
      <div className="workspace-tab-panel-copy">
        <span>Current workspace view</span>
        <strong>{activeTab}</strong>
        <p>{focus}</p>
      </div>
      <div className="workspace-tab-panel-meta">
        <b>{recordCount}</b>
        <span>records in {title.toLowerCase()}</span>
      </div>
    </section>
  );
}
function WorkspaceToolbar({
  spec,
  rows,
  query,
  setQuery,
  statusFilter,
  setStatusFilter,
  execute,
}: {
  spec: ProctorWorkspaceSpec;
  rows: WorkspaceRow[];
  query: string;
  setQuery: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  execute: (action: ProctorActionSpec) => void;
}) {
  const statuses = Array.from(new Set(rows.map(row => rowStatus(row, spec)).filter(Boolean)));
  return (
    <div className="v2-toolbar">
      <div className="input-wrap">
        <Icon name="search" size={14} />
        <input
          className="input"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={`Search ${spec.title.toLowerCase()}…`}
        />
      </div>
      <select
        className="select compact-select"
        value={statusFilter}
        onChange={event => setStatusFilter(event.target.value)}
      >
        <option>All states</option>
        {statuses.map(status => (
          <option key={status}>{status}</option>
        ))}
      </select>
      <Button
        small
        icon="download"
        onClick={() => execute({ label: "Export current view", intent: "export:view", icon: "download" })}
      >
        Export
      </Button>
    </div>
  );
}
function WorkspaceNotes({ notes }: { notes: ProctorNoteSpec[] }) {
  return (
    <div className="v2-note-grid">
      {notes.map(note => (
        <article className="v2-note" key={note.title}>
          <span>
            <Icon name={note.icon} />
          </span>
          <div>
            <b>{note.title}</b>
            <p>{note.body}</p>
          </div>
        </article>
      ))}
    </div>
  );
}
function WorkflowList({
  items,
  checks,
  toggleCheck,
}: {
  items: ProctorWorkflowSpec[];
  checks: Record<string, boolean>;
  toggleCheck: (key: string) => void;
}) {
  return (
    <div className="v2-workflow">
      {items.map((item, index) => {
        const key = `workflow-${index}`;
        const done =
          checks[key] ||
          item.status.toLowerCase().includes("complete") ||
          item.status.toLowerCase().includes("acknowledged");
        return (
          <button
            type="button"
            className={`v2-workflow-row ${done ? "done" : ""}`}
            key={key}
            onClick={() => toggleCheck(key)}
          >
            <span className="v2-step">{done ? <Icon name="check" size={13} /> : index + 1}</span>
            <span className="v2-workflow-copy">
              <b>{item.title}</b>
              <small>{item.detail}</small>
            </span>
            <span className="v2-workflow-owner">
              <small>{item.owner}</small>
              <Status>{done ? "Complete" : item.status}</Status>
            </span>
          </button>
        );
      })}
    </div>
  );
}
function WorkspaceTable({
  spec,
  rows,
  openRow,
  advanceRow,
}: {
  spec: ProctorWorkspaceSpec;
  rows: WorkspaceRow[];
  openRow: (row: WorkspaceRow) => void;
  advanceRow: (row: WorkspaceRow) => void;
}) {
  return (
    <div className="table-wrap v2-table-wrap">
      <table>
        <thead>
          <tr>
            {spec.columns.map(column => (
              <th key={column.key}>{column.label}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${spec.id}-${index}-${rowTitle(row)}`}>
              <>
                {spec.columns.map(column => (
                  <td key={column.key}>
                    {column.key === spec.statusKey ||
                    column.key === "status" ||
                    column.key === "state" ||
                    column.key === "severity" ? (
                      <Status>{rowValue(row, column.key)}</Status>
                    ) : (
                      <span
                        className={
                          column.key === "name" || column.key === "title" || column.key === "summary"
                            ? "v2-cell-primary"
                            : ""
                        }
                      >
                        {rowValue(row, column.key)}
                      </span>
                    )}
                  </td>
                ))}
              </>
              <td>
                <div className="v2-row-actions">
                  <Button small icon="eye" onClick={() => openRow(row)}>
                    Open
                  </Button>
                  <Button small icon="arrow" onClick={() => advanceRow(row)}>
                    Advance
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length ? (
        <EmptyState
          icon="search"
          title="No matching operational records"
          text="Change the search or status filter to view more assigned records."
        />
      ) : null}
    </div>
  );
}
function AnalyticsChart({ values, label = "Operational trend" }: { values: number[]; label?: string }) {
  return (
    <div className="v2-analytics-chart" aria-label={label}>
      {values.map((value, index) => (
        <div className="v2-chart-column" key={index}>
          <div className="v2-chart-bar" style={{ height: `${Math.max(8, value)}%` }}>
            <span>{value}%</span>
          </div>
          <small>{index + 1}</small>
        </div>
      ))}
    </div>
  );
}
function DashboardLayout(props: WorkspaceLayoutProps) {
  const { spec, filtered, openRow, execute, checks, toggleCheck } = props;
  return (
    <div className="dashboard-grid">
      <Card
        title={`${spec.title} operational view`}
        subtitle="Live scope, blockers and human-owned next actions."
      >
        <AnalyticsChart values={spec.chart} />
        <div className="v2-action-list">
          {filtered.slice(0, 5).map((row, index) => (
            <button type="button" className="v2-action-row" key={index} onClick={() => openRow(row)}>
              <span>
                <Icon name={index % 2 ? "pulse" : "shield"} />
              </span>
              <span>
                <b>{rowTitle(row)}</b>
                <small>{rowDetail(row)}</small>
              </span>
              <Status>{rowStatus(row, spec)}</Status>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Named workflow" subtitle="Every consequential step remains attributable.">
          <WorkflowList items={spec.workflow} checks={checks} toggleCheck={toggleCheck} />
        </Card>
        <Card title="Role boundary">
          <WorkspaceNotes notes={spec.notes} />
        </Card>
        <Button icon={spec.primary.icon} variant="primary" onClick={() => execute(spec.primary, filtered[0])}>
          {spec.primary.label}
        </Button>
      </div>
    </div>
  );
}
function QueueLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="generic-board">
      <Card
        title={`${props.spec.title} queue`}
        subtitle="Filter assigned records, open context and move only the operational state you control."
      >
        <WorkspaceToolbar
          spec={props.spec}
          rows={props.rows}
          query={props.query}
          setQuery={props.setQuery}
          statusFilter={props.statusFilter}
          setStatusFilter={props.setStatusFilter}
          execute={props.execute}
        />
        <WorkspaceTable
          spec={props.spec}
          rows={props.filtered}
          openRow={props.openRow}
          advanceRow={props.advanceRow}
        />
      </Card>
      <div>
        <Card title="Triage discipline">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Decision boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function CardsLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-card-grid">
        {props.filtered.map((row, index) => (
          <article className="v2-record-card" key={index}>
            <div className="v2-record-top">
              <span className="list-icon">
                <Icon name={index % 3 === 0 ? props.spec.icon : index % 3 === 1 ? "calendar" : "shield"} />
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </div>
            <h3>{rowTitle(row)}</h3>
            <p>{rowDetail(row)}</p>
            <div className="v2-record-meta">
              {props.spec.columns.slice(1, 5).map(column => (
                <span key={column.key}>
                  <small>{column.label}</small>
                  <b>{rowValue(row, column.key)}</b>
                </span>
              ))}
            </div>
            <div className="record-actions">
              <Button small icon="eye" onClick={() => props.openRow(row)}>
                Open
              </Button>
              <Button small icon="arrow" onClick={() => props.advanceRow(row)}>
                Advance
              </Button>
            </div>
          </article>
        ))}
      </div>
      <Card
        title="Operational workflow"
        subtitle="The next action stays connected to the assigned session and human authority."
      >
        <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
      </Card>
    </>
  );
}
function TimelineLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} timeline`}
        subtitle="Chronological, attributable operational history."
      >
        <div className="v2-timeline">
          {props.filtered.map((row, index) => (
            <button type="button" className="v2-timeline-row" key={index} onClick={() => props.openRow(row)}>
              <span className="v2-timeline-line">
                <i />
              </span>
              <span className="v2-timeline-time">
                {rowValue(row, "time") !== "—" ? rowValue(row, "time") : String(index + 1).padStart(2, "0")}
              </span>
              <span className="v2-timeline-copy">
                <b>{rowTitle(row)}</b>
                <small>{rowDetail(row)}</small>
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Upcoming checkpoint">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Continuity controls">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function BoardLayout(props: WorkspaceLayoutProps) {
  const lanes = ["Incoming", "Assigned", "Human review", "Complete"];
  const grouped = lanes.map((lane, laneIndex) => ({
    lane,
    items: props.filtered.filter((_, index) => index % lanes.length === laneIndex),
  }));
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-board">
        {grouped.map(group => (
          <section className="v2-board-column" key={group.lane}>
            <header>
              <b>{group.lane}</b>
              <span>{group.items.length}</span>
            </header>
            {group.items.map((row, index) => (
              <article className="v2-board-card" key={index}>
                <small>{rowValue(row, "id") !== "—" ? rowValue(row, "id") : props.spec.group}</small>
                <h4>{rowTitle(row)}</h4>
                <p>{rowDetail(row)}</p>
                <div>
                  <span>{rowOwner(row)}</span>
                  <Status>{rowStatus(row, props.spec)}</Status>
                </div>
                <footer>
                  <Button small icon="eye" onClick={() => props.openRow(row)}>
                    Open
                  </Button>
                  <Button small icon="arrow" onClick={() => props.advanceRow(row)}>
                    Move
                  </Button>
                </footer>
              </article>
            ))}
          </section>
        ))}
      </div>
    </>
  );
}
function HealthLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} services`}
        subtitle="Operational dependencies remain separated by platform and purpose."
      >
        <div className="v2-health-grid">
          {props.filtered.map((row, index) => {
            const healthy = !/warn|attention|preflight|monitor|fail/i.test(rowStatus(row, props.spec));
            const value = Math.max(48, 96 - index * 5);
            return (
              <button type="button" className="v2-health-card" key={index} onClick={() => props.openRow(row)}>
                <span className={`v2-health-icon ${healthy ? "healthy" : "watch"}`}>
                  <Icon name={healthy ? "check" : "pulse"} />
                </span>
                <span>
                  <b>{rowTitle(row)}</b>
                  <small>{rowDetail(row)}</small>
                  <Progress value={value} green={healthy} />
                </span>
                <Status>{rowStatus(row, props.spec)}</Status>
              </button>
            );
          })}
        </div>
        <AnalyticsChart values={props.spec.chart} label="Service reliability trend" />
      </Card>
      <div>
        <Card title="Diagnostic actions">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Platform boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function MatrixLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-matrix">
        <div className="v2-matrix-head">
          <span>Assignment</span>
          <span>Scope</span>
          <span>Authority</span>
          <span>Coverage</span>
          <span>State</span>
        </div>
        {props.filtered.map((row, index) => (
          <button type="button" className="v2-matrix-row" key={index} onClick={() => props.openRow(row)}>
            <span>
              <b>{rowTitle(row)}</b>
              <small>
                {rowValue(row, "date")} · {rowValue(row, "time")}
              </small>
            </span>
            <span>{rowDetail(row)}</span>
            <span>{rowValue(row, "role") !== "—" ? rowValue(row, "role") : rowOwner(row)}</span>
            <span>
              {rowValue(row, "candidates") !== "—"
                ? `${rowValue(row, "candidates")} candidates`
                : rowValue(row, "owner")}
            </span>
            <Status>{rowStatus(row, props.spec)}</Status>
          </button>
        ))}
      </div>
    </>
  );
}
function BriefLayout(props: WorkspaceLayoutProps) {
  const row = props.filtered[0] || props.rows[0] || {};
  return (
    <div className="dashboard-grid">
      <Card
        title="Assessment brief"
        subtitle="One session record combines scope, timing, approved materials, accessibility instructions and escalation contacts."
      >
        <div className="v2-brief-hero">
          <span className="list-icon">
            <Icon name={props.spec.icon} />
          </span>
          <div>
            <small>{rowValue(row, "program")}</small>
            <h3>{rowTitle(row)}</h3>
            <p>{rowDetail(row)}</p>
          </div>
          <Status>{rowStatus(row, props.spec)}</Status>
        </div>
        <div className="v2-detail-grid">
          {props.spec.columns.slice(1).map(column => (
            <div key={column.key}>
              <small>{column.label}</small>
              <b>{rowValue(row, column.key)}</b>
            </div>
          ))}
        </div>
        <div className="record-actions">
          <Button icon="eye" variant="primary" onClick={() => props.openRow(row)}>
            Open complete session
          </Button>
          <Button icon="users" onClick={() => props.cb.navigate("readiness", "candidate-roster")}>
            Candidate scope
          </Button>
        </div>
      </Card>
      <div>
        <Card title="Preflight sequence">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Authority and privacy">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function CalendarLayout(props: WorkspaceLayoutProps) {
  const [monthShift, setMonthShift] = useState(0);
  const base = new Date(2026, 9 + monthShift, 1);
  const start = base.getDay();
  const days = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: 42 }, (_, index) => index - start + 1);
  return (
    <div className="dashboard-grid">
      <Card
        title={base.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        subtitle="Assessment shifts, preflights, rehearsals, relief windows and training."
      >
        <div className="v2-calendar-toolbar">
          <Button small icon="chevron" onClick={() => setMonthShift(value => value - 1)}>
            Previous
          </Button>
          <span />
          <Button small icon="arrow" onClick={() => setMonthShift(value => value + 1)}>
            Next
          </Button>
        </div>
        <div className="v2-calendar">
          <div className="v2-calendar-labels">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <b key={day}>{day}</b>
            ))}
          </div>
          <div className="v2-calendar-days">
            {cells.map((day, index) => (
              <div className={`v2-calendar-day ${day < 1 || day > days ? "muted" : ""}`} key={index}>
                <span>{day > 0 && day <= days ? day : ""}</span>
                {day > 0 && day <= days && day % 5 === 0 ? (
                  <button type="button" onClick={() => props.execute(props.spec.primary)}>
                    {props.rows[index % Math.max(1, props.rows.length)]
                      ? rowTitle(props.rows[index % props.rows.length]).slice(0, 25)
                      : "Proctor preflight"}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </Card>
      <div>
        <Card title="Selected agenda">
          <div className="v2-action-list">
            {props.filtered.slice(0, 6).map((row, index) => (
              <button type="button" className="v2-action-row" key={index} onClick={() => props.openRow(row)}>
                <span>
                  <Icon name="calendar" />
                </span>
                <span>
                  <b>{rowTitle(row)}</b>
                  <small>
                    {rowValue(row, "date")} · {rowValue(row, "time")}
                  </small>
                </span>
                <Status>{rowStatus(row, props.spec)}</Status>
              </button>
            ))}
          </div>
        </Card>
        <Card title="Scheduling boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function DirectoryLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-directory-grid">
        {props.filtered.map((row, index) => (
          <article className="v2-person-card" key={index}>
            <div className="v2-person-head">
              <span className="avatar">
                {rowTitle(row)
                  .split(/\s+/)
                  .map(value => value[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <div>
                <b>{rowTitle(row)}</b>
                <small>{rowDetail(row)}</small>
              </div>
              <i className="presence online" />
            </div>
            <div className="v2-person-details">
              {props.spec.columns.slice(1, 5).map(column => (
                <span key={column.key}>
                  <small>{column.label}</small>
                  <b>{rowValue(row, column.key)}</b>
                </span>
              ))}
            </div>
            <footer>
              <Button small icon="message" onClick={() => props.openRow(row)}>
                Open
              </Button>
              <Button small icon="arrow" onClick={() => props.advanceRow(row)}>
                Update
              </Button>
            </footer>
          </article>
        ))}
      </div>
    </>
  );
}
function RosterLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-roster-summary">
        <span>
          <b>{props.filtered.length}</b>
          <small>Visible records</small>
        </span>
        <span>
          <b>
            {
              props.filtered.filter(row =>
                /normal|ready|verified|yes|true/i.test(
                  rowStatus(row, props.spec) + rowValue(row, "identity"),
                ),
              ).length
            }
          </b>
          <small>Ready / normal</small>
        </span>
        <span>
          <b>
            {
              props.filtered.filter(row =>
                /review|re-check|support|fair|no|false/i.test(Object.values(row).join(" ")),
              ).length
            }
          </b>
          <small>Human attention</small>
        </span>
        <span>
          <b>0</b>
          <small>Automated outcomes</small>
        </span>
      </div>
      <WorkspaceTable
        spec={props.spec}
        rows={props.filtered.slice(0, 40)}
        openRow={props.openRow}
        advanceRow={props.advanceRow}
      />
    </>
  );
}
function VerificationLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} queue`}
        subtitle="A human performs the approved check or uses a documented accessible alternative."
      >
        <div className="v2-verification-list">
          {props.filtered.slice(0, 12).map((row, index) => (
            <button
              type="button"
              className="v2-verification-row"
              key={index}
              onClick={() => props.openRow(row)}
            >
              <span className="avatar">
                {rowTitle(row)
                  .split(/\s+/)
                  .map(value => value[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span>
                <b>{rowTitle(row)}</b>
                <small>
                  {rowValue(row, "candidateNo")} · {rowDetail(row)}
                </small>
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Verification sequence">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button
            icon={props.spec.primary.icon}
            variant="primary"
            onClick={() => props.execute(props.spec.primary, props.filtered[0])}
          >
            {props.spec.primary.label}
          </Button>
        </Card>
        <Card title="Non-negotiable boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function DiagnosticsLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-diagnostic-grid">
        {props.filtered.slice(0, 24).map((row, index) => (
          <article className="v2-diagnostic-card" key={index}>
            <header>
              <span className="avatar">
                {rowTitle(row)
                  .split(/\s+/)
                  .map(value => value[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <span>
                <b>{rowTitle(row)}</b>
                <small>{rowValue(row, "candidateNo")}</small>
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </header>
            <div className="v2-device-states">
              <span>
                <Icon name="monitor" size={13} />
                <b>Browser</b>
                <small>Ready</small>
              </span>
              <span>
                <Icon name="camera" size={13} />
                <b>Camera</b>
                <small>{rowValue(row, "camera")}</small>
              </span>
              <span>
                <Icon name="mic" size={13} />
                <b>Microphone</b>
                <small>{rowValue(row, "microphone")}</small>
              </span>
              <span>
                <Icon name="pulse" size={13} />
                <b>Network</b>
                <small>{rowValue(row, "network")}</small>
              </span>
            </div>
            <Progress value={Math.max(45, 96 - (index % 7) * 6)} green={index % 5 !== 0} />
            <footer>
              <Button small icon="pulse" onClick={() => props.execute(props.spec.primary, row)}>
                Run check
              </Button>
              <Button small icon="eye" onClick={() => props.openRow(row)}>
                Details
              </Button>
            </footer>
          </article>
        ))}
      </div>
    </>
  );
}
function ChecklistLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="generic-board">
      <Card
        title={`${props.spec.title} checklist`}
        subtitle="Checks are proportionate, published and limited to the assigned assessment purpose."
      >
        <div className="v2-checklist-grid">
          {(props.filtered.length ? props.filtered : (props.spec.workflow as unknown as WorkspaceRow[]))
            .slice(0, 16)
            .map((row, index) => {
              const key = `check-${index}`;
              const checked = !!props.checks[key];
              return (
                <button
                  type="button"
                  className={`v2-check-card ${checked ? "checked" : ""}`}
                  key={index}
                  onClick={() => props.toggleCheck(key)}
                >
                  <span className="v2-check-box">{checked ? <Icon name="check" size={13} /> : null}</span>
                  <span>
                    <b>{rowTitle(row)}</b>
                    <small>{rowDetail(row) || String((row as any).detail || "")}</small>
                  </span>
                  <Status>{checked ? "Complete" : rowStatus(row, props.spec)}</Status>
                </button>
              );
            })}
        </div>
      </Card>
      <div>
        <Card title="Completion">
          <div className="v2-completion-ring">
            <strong>
              {Math.round((Object.values(props.checks).filter(Boolean).length / Math.max(1, 16)) * 100)}%
            </strong>
            <span>locally acknowledged</span>
          </div>
          <Button
            icon={props.spec.primary.icon}
            variant="primary"
            onClick={() => props.execute(props.spec.primary, props.filtered[0])}
          >
            {props.spec.primary.label}
          </Button>
        </Card>
        <Card title="Boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function RegisterLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <WorkspaceTable
        spec={props.spec}
        rows={props.filtered}
        openRow={props.openRow}
        advanceRow={props.advanceRow}
      />
      <Card
        title="Register governance"
        subtitle="Every rule or record has a named authority and a versioned operational purpose."
      >
        <WorkspaceNotes notes={props.spec.notes} />
      </Card>
    </>
  );
}
function GridLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-candidate-grid">
        {props.filtered.slice(0, 36).map((row, index) => (
          <button
            type="button"
            className={`v2-candidate-tile ${statusTone(rowStatus(row, props.spec))}`}
            key={index}
            onClick={() => props.openRow(row)}
          >
            <div className="v2-video-placeholder">
              <span className="avatar">
                {rowTitle(row)
                  .split(/\s+/)
                  .map(value => value[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <div>
                <span>
                  <Icon name={rowValue(row, "camera") === "Yes" ? "camera" : "alert"} size={11} />
                  {rowValue(row, "camera") === "Yes" ? "Camera" : "Check"}
                </span>
                <span>
                  <Icon name="pulse" size={11} />
                  {rowValue(row, "network")}
                </span>
              </div>
            </div>
            <footer>
              <span>
                <b>{rowTitle(row)}</b>
                <small>
                  {rowValue(row, "candidateNo")} · {rowValue(row, "timeRemaining")}
                </small>
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </footer>
          </button>
        ))}
      </div>
    </>
  );
}
function PanelLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title="Live oral-assessment turn queue"
        subtitle="The proctor controls procedure and continuity. Assessors retain rubric and academic decision authority."
      >
        <div className="v2-panel-stage">
          <div className="v2-live-candidate">
            <span className="avatar">LN</span>
            <div>
              <small>Candidate currently in examination</small>
              <b>Linda Nsubuga</b>
              <p>Responsible AI oral defence · question 3 of 5 · 14 minutes remaining</p>
            </div>
            <Status>Live</Status>
          </div>
          <div className="v2-panel-members">
            {["Chair", "Assessor 1", "Assessor 2", "Proctor", "Captioner"].map((role, index) => (
              <span key={role}>
                <i>
                  {role
                    .split(/\s/)
                    .map(v => v[0])
                    .join("")}
                </i>
                <b>{role}</b>
                <small>{index === 3 ? "Procedure only" : "Assigned authority"}</small>
              </span>
            ))}
          </div>
        </div>
        <div className="v2-turn-queue">
          {props.filtered.slice(0, 8).map((row, index) => (
            <button type="button" key={index} onClick={() => props.openRow(row)}>
              <span>{index + 1}</span>
              <span>
                <b>{rowTitle(row)}</b>
                <small>
                  {rowValue(row, "time")} · {rowValue(row, "lead")}
                </small>
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Panel controls">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button
            icon="video"
            variant="primary"
            onClick={() => props.execute(props.spec.primary, props.filtered[0])}
          >
            Open assigned Class room
          </Button>
        </Card>
        <Card title="Authority separation">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function MediaLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-media-grid">
        {props.filtered.map((row, index) => (
          <article className="v2-media-card" key={index}>
            <div className="v2-media-preview">
              <Icon name={index % 2 ? "camera" : "video"} size={30} />
              <button type="button" onClick={() => props.openRow(row)}>
                <Icon name="play" />
              </button>
              <span>{index % 2 ? "Image evidence" : "Authorized media"}</span>
            </div>
            <div className="v2-media-body">
              <Status>{rowStatus(row, props.spec)}</Status>
              <h3>{rowTitle(row)}</h3>
              <p>{rowDetail(row)}</p>
              <div className="v2-record-meta">
                {props.spec.columns.slice(1, 4).map(column => (
                  <span key={column.key}>
                    <small>{column.label}</small>
                    <b>{rowValue(row, column.key)}</b>
                  </span>
                ))}
              </div>
              <footer>
                <Button small icon="eye" onClick={() => props.openRow(row)}>
                  Inspect
                </Button>
                <Button
                  small
                  icon="download"
                  onClick={() =>
                    props.execute(
                      { label: "Request authorized access", intent: "modal:secure-note", icon: "lock" },
                      row,
                    )
                  }
                >
                  Access
                </Button>
              </footer>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
function PolicyLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} register`}
        subtitle="Versioned rules, named authorities and acknowledgement history."
      >
        <WorkspaceToolbar
          spec={props.spec}
          rows={props.rows}
          query={props.query}
          setQuery={props.setQuery}
          statusFilter={props.statusFilter}
          setStatusFilter={props.setStatusFilter}
          execute={props.execute}
        />
        <div className="v2-policy-list">
          {props.filtered.map((row, index) => {
            const key = `policy-${index}`;
            const acknowledged =
              props.checks[key] || /acknowledged|current|allowed|ready/i.test(rowStatus(row, props.spec));
            return (
              <article className="v2-policy-row" key={index}>
                <span>
                  <Icon name={acknowledged ? "check" : "book"} />
                </span>
                <div>
                  <b>{rowTitle(row)}</b>
                  <p>{rowDetail(row)}</p>
                  <small>
                    {rowValue(row, "version")} · {rowOwner(row)}
                  </small>
                </div>
                <Status>{acknowledged ? "Acknowledged" : rowStatus(row, props.spec)}</Status>
                <Button small icon="check" onClick={() => props.toggleCheck(key)}>
                  {acknowledged ? "Review" : "Acknowledge"}
                </Button>
              </article>
            );
          })}
        </div>
      </Card>
      <div>
        <Card title="Policy workflow">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Non-delegable boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function EnvironmentLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-environment-grid">
        {props.filtered.map((row, index) => (
          <article className="v2-environment-card" key={index}>
            <header>
              <span>
                <Icon name="flask" />
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </header>
            <h3>{rowTitle(row)}</h3>
            <p>{rowDetail(row)}</p>
            <div className="v2-environment-map">
              <span>
                <Icon name="lock" size={12} />
                Entitlement
              </span>
              <i />
              <span>
                <Icon name="sliders" size={12} />
                Approved tools
              </span>
              <i />
              <span>
                <Icon name="database" size={12} />
                Evidence return
              </span>
            </div>
            <footer>
              <Button small icon="eye" onClick={() => props.openRow(row)}>
                Inspect
              </Button>
              <Button small icon="external" onClick={() => props.execute(props.spec.primary, row)}>
                Open Labs
              </Button>
            </footer>
          </article>
        ))}
      </div>
    </>
  );
}
function PracticalLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title="Practical runtime control"
        subtitle="Runtime status, support signals and candidate-scoped continuity."
      >
        <div className="v2-runtime-stage">
          <header>
            <span className="live-pulse">
              <i />
            </span>
            <div>
              <b>Agentic Workflow Practical Examination</b>
              <small>24 candidate runtimes · intended Lab environment</small>
            </div>
            <Status>Live</Status>
          </header>
          <div className="v2-runtime-grid">
            {props.filtered.slice(0, 12).map((row, index) => (
              <button type="button" key={index} onClick={() => props.openRow(row)}>
                <span className="avatar">
                  {rowTitle(row)
                    .split(/\s+/)
                    .map(v => v[0])
                    .slice(0, 2)
                    .join("")}
                </span>
                <span>
                  <b>{rowTitle(row)}</b>
                  <small>{rowValue(row, "lastEvent")}</small>
                  <Progress value={Math.max(42, 88 - (index % 5) * 7)} green={index % 4 !== 0} />
                </span>
                <Status>{rowStatus(row, props.spec)}</Status>
              </button>
            ))}
          </div>
        </div>
      </Card>
      <div>
        <Card title="Runtime attention queue">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button
            icon="external"
            variant="primary"
            onClick={() => props.execute(props.spec.primary, props.filtered[0])}
          >
            Open Labs Exam Room
          </Button>
        </Card>
        <Card title="Labs boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function EvidenceLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="generic-board">
      <Card
        title={`${props.spec.title} records`}
        subtitle="Evidence references remain linked to source, integrity, custody and purpose."
      >
        <WorkspaceToolbar
          spec={props.spec}
          rows={props.rows}
          query={props.query}
          setQuery={props.setQuery}
          statusFilter={props.statusFilter}
          setStatusFilter={props.setStatusFilter}
          execute={props.execute}
        />
        <div className="v2-evidence-grid">
          {props.filtered.map((row, index) => (
            <article className="v2-evidence-card" key={index}>
              <header>
                <span>
                  <Icon name="database" />
                </span>
                <div>
                  <small>{rowValue(row, "id")}</small>
                  <b>{rowTitle(row)}</b>
                </div>
                <Status>{rowStatus(row, props.spec)}</Status>
              </header>
              <p>{rowDetail(row)}</p>
              <div>
                <span>
                  <small>Source</small>
                  <b>{rowValue(row, "source")}</b>
                </span>
                <span>
                  <small>Integrity</small>
                  <b>{rowValue(row, "hash")}</b>
                </span>
                <span>
                  <small>Custodian</small>
                  <b>{rowValue(row, "custodian")}</b>
                </span>
              </div>
              <footer>
                <Button small icon="eye" onClick={() => props.openRow(row)}>
                  Inspect
                </Button>
                <Button small icon="link" onClick={() => props.cb.navigate("evidence", "chain-of-custody")}>
                  Custody
                </Button>
              </footer>
            </article>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Review workflow">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Evidence boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function BoundaryLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} data-flow map`}
        subtitle="Allowed data, protected content and authority boundaries are explicit."
      >
        <div className="v2-boundary-map">
          {props.filtered.map((row, index) => (
            <div className="v2-boundary-row" key={index}>
              <span className="v2-boundary-node">
                <Icon name={index % 3 === 0 ? "shield" : index % 3 === 1 ? "lock" : "database"} />
                <b>{rowTitle(row)}</b>
              </span>
              <span className="v2-boundary-arrow">
                <Icon name="arrow" />
              </span>
              <span className="v2-boundary-rule">
                <small>
                  {rowValue(row, "boundary") !== "—" ? rowValue(row, "boundary") : rowValue(row, "purpose")}
                </small>
                <p>{rowDetail(row)}</p>
              </span>
              <Status>{rowStatus(row, props.spec)}</Status>
            </div>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Permitted operational workflow">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Data minimization">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function FormLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="generic-board">
      <Card
        title={`${props.spec.title} form`}
        subtitle="Required operational detail is validated before a consequential record or message is created."
      >
        <form
          onSubmit={event => {
            event.preventDefault();
            props.saveForm();
          }}
        >
          <div className="v2-form-grid">
            {props.spec.fields.map((field, index) => (
              <label
                className={index % 5 === 3 || index === props.spec.fields.length - 1 ? "wide" : ""}
                key={field}
              >
                <span>{field}</span>
                {index % 5 === 3 ? (
                  <textarea
                    className="textarea"
                    value={props.formValues[field] || ""}
                    onChange={event =>
                      props.setFormValues(values => ({ ...values, [field]: event.target.value }))
                    }
                  />
                ) : (
                  <input
                    className="input"
                    value={props.formValues[field] || ""}
                    onChange={event =>
                      props.setFormValues(values => ({ ...values, [field]: event.target.value }))
                    }
                  />
                )}
              </label>
            ))}
          </div>
          <div className="record-actions">
            <Button type="submit" icon={props.spec.primary.icon} variant="primary">
              {props.spec.primary.label}
            </Button>
            <Button
              icon="shield"
              onClick={() =>
                props.execute({ label: "Open role boundary", intent: "modal:scope-boundary", icon: "shield" })
              }
            >
              Review authority
            </Button>
          </div>
        </form>
      </Card>
      <div>
        <Card title="Before submission">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Required separation">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function StatementsLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <WorkspaceToolbar
        spec={props.spec}
        rows={props.rows}
        query={props.query}
        setQuery={props.setQuery}
        statusFilter={props.statusFilter}
        setStatusFilter={props.setStatusFilter}
        execute={props.execute}
      />
      <div className="v2-statement-grid">
        {props.filtered.map((row, index) => (
          <article className="v2-statement-card" key={index}>
            <header>
              <span className="avatar">
                {rowTitle(row)
                  .split(/\s+/)
                  .map(value => value[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <div>
                <b>{rowTitle(row)}</b>
                <small>{rowValue(row, "time")}</small>
              </div>
              <Status>{rowStatus(row, props.spec)}</Status>
            </header>
            <blockquote>{rowDetail(row)}</blockquote>
            <footer>
              <span>
                <Icon name="globe" size={12} />
                Original language retained
              </span>
              <Button small icon="eye" onClick={() => props.openRow(row)}>
                Open
              </Button>
            </footer>
          </article>
        ))}
      </div>
      <Card title="Candidate-statement safeguards">
        <WorkspaceNotes notes={props.spec.notes} />
      </Card>
    </>
  );
}
function LogsLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title="Technical log explorer"
        subtitle="Connection and device logs remain operational evidence, not integrity findings."
      >
        <WorkspaceToolbar
          spec={props.spec}
          rows={props.rows}
          query={props.query}
          setQuery={props.setQuery}
          statusFilter={props.statusFilter}
          setStatusFilter={props.setStatusFilter}
          execute={props.execute}
        />
        <div className="v2-log-console">
          {props.filtered.map((row, index) => (
            <button type="button" key={index} onClick={() => props.openRow(row)}>
              <code>{rowValue(row, "captured")}</code>
              <span>{rowTitle(row)}</span>
              <span>{rowValue(row, "candidate")}</span>
              <code>{rowValue(row, "hash")}</code>
              <Status>{rowStatus(row, props.spec)}</Status>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Log interpretation">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Technical separation">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function CustodyLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title="Evidence custody ledger"
        subtitle="Every seal, view, transfer, export and disposition is attributable."
      >
        <div className="v2-custody-chain">
          {props.filtered.map((row, index) => (
            <button type="button" key={index} onClick={() => props.openRow(row)}>
              <span className="v2-custody-index">{index + 1}</span>
              <span>
                <b>{rowTitle(row)}</b>
                <small>
                  {rowValue(row, "captured")} · {rowDetail(row)}
                </small>
              </span>
              <span>
                <small>Custodian</small>
                <b>{rowValue(row, "custodian")}</b>
              </span>
              <code>{rowValue(row, "hash")}</code>
              <Status>{rowStatus(row, props.spec)}</Status>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Transfer checkpoint">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button
            icon={props.spec.primary.icon}
            variant="primary"
            onClick={() => props.execute(props.spec.primary, props.filtered[0])}
          >
            {props.spec.primary.label}
          </Button>
        </Card>
        <Card title="Custody boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function BuilderLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} configuration`}
        subtitle="Select purpose, scope and authority before generating a preview."
      >
        <div className="v2-form-grid">
          {props.spec.fields.map((field, index) => (
            <label
              className={index === 2 || index === props.spec.fields.length - 1 ? "wide" : ""}
              key={field}
            >
              <span>{field}</span>
              {index === 2 ? (
                <textarea
                  className="textarea"
                  value={props.formValues[field] || ""}
                  onChange={event =>
                    props.setFormValues(values => ({ ...values, [field]: event.target.value }))
                  }
                />
              ) : (
                <input
                  className="input"
                  value={props.formValues[field] || ""}
                  onChange={event =>
                    props.setFormValues(values => ({ ...values, [field]: event.target.value }))
                  }
                />
              )}
            </label>
          ))}
        </div>
        <div className="record-actions">
          <Button
            icon="eye"
            onClick={() =>
              props.cb.toast(
                "Preview refreshed",
                "The report or export preview now reflects the selected purpose and fields.",
                "good",
              )
            }
          >
            Preview
          </Button>
          <Button icon={props.spec.primary.icon} variant="primary" onClick={props.saveForm}>
            {props.spec.primary.label}
          </Button>
        </div>
      </Card>
      <div>
        <Card title="Generated preview">
          <div className="v2-document-preview">
            <span className="v2-document-logo">CA</span>
            <small>CHATONER AI ACADEMY</small>
            <h3>{props.spec.title}</h3>
            <p>
              Purpose-scoped operational document. Candidate outcomes, protected diagnoses and unrelated
              records are excluded.
            </p>
            <div>
              {props.spec.metrics.map(item => (
                <span key={item.label}>
                  <b>{item.value}</b>
                  <small>{item.label}</small>
                </span>
              ))}
            </div>
          </div>
        </Card>
        <Card title="Approval path">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
      </div>
    </div>
  );
}
function TimersLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <div className="v2-timer-grid">
        {props.filtered.map((row, index) => (
          <article className="v2-timer-card" key={index}>
            <header>
              <span className="avatar">
                {rowTitle(row)
                  .split(/\s+/)
                  .map(value => value[0])
                  .slice(0, 2)
                  .join("")}
              </span>
              <div>
                <b>{rowTitle(row)}</b>
                <small>{rowValue(row, "type")}</small>
              </div>
              <Status>{rowStatus(row, props.spec)}</Status>
            </header>
            <div className="v2-clock">
              {index % 3 === 0 ? "01:48:22" : index % 3 === 1 ? "00:32:15" : "Paused"}
            </div>
            <p>{rowValue(row, "instructions")}</p>
            <footer>
              <Button small icon="clock" onClick={() => props.execute(props.spec.primary, row)}>
                Apply approved action
              </Button>
              <Button small icon="history" onClick={() => props.openRow(row)}>
                History
              </Button>
            </footer>
          </article>
        ))}
      </div>
      <Card title="Timer and break controls">
        <WorkspaceNotes notes={props.spec.notes} />
      </Card>
    </>
  );
}
function SupportLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="generic-board">
      <Card
        title={`${props.spec.title} queue`}
        subtitle="Neutral procedural, technical and accessibility support stays separate from academic judgment."
      >
        <WorkspaceToolbar
          spec={props.spec}
          rows={props.rows}
          query={props.query}
          setQuery={props.setQuery}
          statusFilter={props.statusFilter}
          setStatusFilter={props.setStatusFilter}
          execute={props.execute}
        />
        <div className="v2-support-list">
          {props.filtered.map((row, index) => (
            <article className="v2-support-row" key={index}>
              <span className={`v2-sla ${index < 2 ? "urgent" : ""}`}>
                {rowValue(row, "priority") !== "—" ? rowValue(row, "priority") : rowValue(row, "sla")}
              </span>
              <div>
                <b>{rowTitle(row)}</b>
                <p>{rowDetail(row)}</p>
                <small>{rowOwner(row)}</small>
              </div>
              <Status>{rowStatus(row, props.spec)}</Status>
              <Button small icon="message" onClick={() => props.openRow(row)}>
                Respond
              </Button>
            </article>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Support sequence">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Role boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function AnalyticsLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} trend`}
        subtitle="Aggregate operational measures are separated from candidate-level academic outcomes."
      >
        <AnalyticsChart values={props.spec.chart} />
        <div className="v2-analytics-legend">
          {props.spec.metrics.map(item => (
            <span key={item.label}>
              <i style={{ background: metricPalette(item.tone).text }} />
              <b>{item.label}</b>
              <small>
                {item.value} · {item.trend}
              </small>
            </span>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Quality actions">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button
            icon="download"
            variant="primary"
            onClick={() =>
              props.execute({
                label: "Export aggregate report",
                intent: "export:analytics",
                icon: "download",
              })
            }
          >
            Export aggregate report
          </Button>
        </Card>
        <Card title="Reporting boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
      <Card title="Underlying operational records" className="v2-full-card">
        <WorkspaceTable
          spec={props.spec}
          rows={props.filtered.slice(0, 12)}
          openRow={props.openRow}
          advanceRow={props.advanceRow}
        />
      </Card>
    </div>
  );
}
function PerformanceLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <div className="v2-score-grid">
        {props.filtered.map((row, index) => {
          const value = parseInt(rowValue(row, "status")) || [96, 91, 100, 100, 88][index % 5];
          return (
            <article className="v2-score-card" key={index}>
              <header>
                <span>
                  <Icon name={index % 2 ? "users" : "clipboard"} />
                </span>
                <Status>{rowValue(row, "target")}</Status>
              </header>
              <h3>{rowTitle(row)}</h3>
              <strong>{value}%</strong>
              <Progress value={value} green={value >= 90} />
              <p>{rowDetail(row)}</p>
              <footer>
                <small>{rowOwner(row)}</small>
                <Button small icon="eye" onClick={() => props.openRow(row)}>
                  Review
                </Button>
              </footer>
            </article>
          );
        })}
      </div>
      <Card title="Development actions">
        <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
      </Card>
    </>
  );
}
function ReviewLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="generic-board">
      <Card
        title={`${props.spec.title} workbench`}
        subtitle="Quality observations become named improvement actions, not candidate-level suspicion scores."
      >
        <div className="v2-review-list">
          {props.filtered.map((row, index) => (
            <article className="v2-review-card" key={index}>
              <header>
                <span>
                  <Icon name={index % 2 ? "check" : "eye"} />
                </span>
                <div>
                  <small>{rowValue(row, "due")}</small>
                  <b>{rowTitle(row)}</b>
                </div>
                <Status>{rowStatus(row, props.spec)}</Status>
              </header>
              <p>{rowDetail(row)}</p>
              <footer>
                <span>{rowOwner(row)}</span>
                <Button small icon="arrow" onClick={() => props.advanceRow(row)}>
                  Advance action
                </Button>
              </footer>
            </article>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Review workflow">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
        </Card>
        <Card title="Quality boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function RunbookLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} protected runbook`}
        subtitle="Safety and protected support take priority over routine monitoring."
      >
        <div className="v2-runbook">
          {props.filtered.map((row, index) => (
            <article key={index}>
              <span>{index + 1}</span>
              <div>
                <small>{rowValue(row, "priority")}</small>
                <h3>{rowTitle(row)}</h3>
                <p>{rowDetail(row)}</p>
                <footer>
                  <Status>{rowStatus(row, props.spec)}</Status>
                  <b>{rowOwner(row)}</b>
                </footer>
              </div>
            </article>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Immediate actions">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button icon="alert" variant="danger" onClick={() => props.execute(props.spec.primary)}>
            Open protected pathway
          </Button>
        </Card>
        <Card title="Visibility boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function CertificationLayout(props: WorkspaceLayoutProps) {
  const renewalActionsComplete = props.spec.workflow.every((item, index) => {
    const status = item.status.toLowerCase();
    return (
      props.checks[`workflow-${index}`] || status.includes("complete") || status.includes("acknowledged")
    );
  });
  return (
    <div className="dashboard-grid">
      <Card
        title="Proctor certification path"
        subtitle="Vetting, policy acknowledgement, scenario practice and human-reviewed calibration."
      >
        <div className="v2-certification-ring">
          <div>
            <strong>92%</strong>
            <span>Current readiness</span>
          </div>
        </div>
        <div className="v2-certification-list">
          {props.filtered.map((row, index) => (
            <button
              type="button"
              key={index}
              className={rowStatus(row, props.spec).toLowerCase().includes("complete") ? "done" : ""}
              onClick={() => props.openRow(row)}
            >
              <span>{index + 1}</span>
              <span>
                <b>{rowTitle(row)}</b>
                <small>{rowDetail(row)}</small>
              </span>
              <span>
                <b>{rowValue(row, "score")}</b>
                <Status>{rowStatus(row, props.spec)}</Status>
              </span>
            </button>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Renewal actions">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button
            icon="award"
            variant="primary"
            disabled={!renewalActionsComplete}
            onClick={() => props.execute(props.spec.primary)}
          >
            Continue certification
          </Button>
        </Card>
        <Card title="Certification authority">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function SimulationLayout(props: WorkspaceLayoutProps) {
  const [scenario, setScenario] = useState(0);
  const [choice, setChoice] = useState("");
  const [feedback, setFeedback] = useState("");
  const scenarios = props.filtered.length ? props.filtered : props.rows;
  const current = scenarios[scenario % Math.max(1, scenarios.length)] || {};
  const choose = (value: string) => {
    setChoice(value);
    setFeedback(
      value.startsWith("A")
        ? "Appropriate: preserve continuity, record facts and use the named human pathway."
        : "Review the authority boundary: do not infer misconduct, grade work or broaden surveillance.",
    );
  };
  return (
    <div className="dashboard-grid">
      <Card title="Practice scenario" subtitle={`${scenario + 1} of ${scenarios.length}`}>
        <div className="v2-simulation">
          <span className="list-icon">
            <Icon name="play" />
          </span>
          <small>{rowStatus(current, props.spec)}</small>
          <h3>{rowTitle(current)}</h3>
          <p>{rowDetail(current)}</p>
          <div className="v2-simulation-options">
            {[
              "A · Support continuity, record observable facts and escalate to the named authority",
              "B · End the candidate’s assessment immediately",
              "C · Decide whether misconduct occurred",
              "D · Inspect unrelated learner and platform records",
            ].map(option => (
              <button
                type="button"
                className={choice === option ? "selected" : ""}
                key={option}
                onClick={() => choose(option)}
              >
                {option}
              </button>
            ))}
          </div>
          {feedback ? (
            <div className={`v2-feedback ${choice.startsWith("A") ? "good" : "warn"}`}>
              <Icon name={choice.startsWith("A") ? "check" : "alert"} />
              <span>{feedback}</span>
            </div>
          ) : null}
        </div>
        <div className="record-actions">
          <Button
            icon="arrow"
            variant="primary"
            disabled={!choice}
            onClick={() => {
              setScenario(value => (value + 1) % Math.max(1, scenarios.length));
              setChoice("");
              setFeedback("");
            }}
          >
            Next scenario
          </Button>
          <Button
            icon="file"
            disabled={!choice}
            onClick={() =>
              props.cb.toast(
                "Practice record saved",
                "Your selection and reviewer-facing rationale were retained.",
                "good",
              )
            }
          >
            Save practice record
          </Button>
        </div>
      </Card>
      <div>
        <Card title="Calibration progress">
          <AnalyticsChart values={[65, 72, 76, 84, 88, 91, 94, 96]} />
        </Card>
        <Card title="Human review">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function SettingsLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title={`${props.spec.title} controls`}
        subtitle="Preferences apply only within allowed policy and assigned-session scope."
      >
        {props.spec.id === "security-sessions" ? (
          <IdentitySecurityControls />
        ) : (
          <>
            <div className="v2-setting-list">
              {(props.rows.length
                ? props.rows
                : props.spec.fields.map(field => ({
                    name: field,
                    detail: `Configure ${field.toLowerCase()} for assigned proctor work.`,
                    status: "Enabled",
                    owner: "Amina Mutesi",
                  }))
              ).map((row, index) => {
                const key = `setting-${index}`;
                const enabled = !!props.checks[key];
                return (
                  <div className="v2-setting-row" key={index}>
                    <span>
                      <Icon name={index % 3 === 0 ? "bell" : index % 3 === 1 ? "shield" : "lock"} />
                    </span>
                    <div>
                      <b>{rowTitle(row)}</b>
                      <p>{rowDetail(row)}</p>
                    </div>
                    <button
                      type="button"
                      className={`toggle ${enabled ? "on" : ""}`}
                      onClick={() => props.toggleCheck(key)}
                      aria-pressed={enabled}
                    >
                      <span />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="record-actions">
              <Button icon="check" variant="primary" onClick={() => props.execute(props.spec.primary)}>
                Save settings
              </Button>
              <Button
                icon="history"
                onClick={() =>
                  props.cb.toast(
                    "Settings audit opened",
                    "Recent preference and security changes are available.",
                  )
                }
              >
                Audit history
              </Button>
            </div>
          </>
        )}
      </Card>
      <div>
        <Card title="Current state">
          <div className="v2-detail-grid">
            {props.spec.metrics.map(item => (
              <div key={item.label}>
                <small>{item.label}</small>
                <b>{item.value}</b>
                <span>{item.trend}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Policy override">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function ConnectionsLayout(props: WorkspaceLayoutProps) {
  return (
    <div className="dashboard-grid">
      <Card
        title="Connected platform services"
        subtitle="Each independent platform validates its own audience, assignment, destination and one-time handoff."
      >
        <div className="v2-connection-list">
          {props.filtered.map((row, index) => (
            <article key={index}>
              <span className="v2-connection-icon">
                <Icon name={index === 1 ? "video" : index === 2 ? "flask" : "shield"} />
              </span>
              <div>
                <small>{rowValue(row, "endpoint")}</small>
                <b>{rowTitle(row)}</b>
                <p>{rowDetail(row)}</p>
              </div>
              <Status>{rowStatus(row, props.spec)}</Status>
              <Button
                small
                icon="pulse"
                onClick={() =>
                  props.cb.toast(
                    `${rowTitle(row)} checked`,
                    `Health, audience, destination and callback status are current.`,
                    "good",
                  )
                }
              >
                Test
              </Button>
            </article>
          ))}
        </div>
      </Card>
      <div>
        <Card title="Handoff workflow">
          <WorkflowList items={props.spec.workflow} checks={props.checks} toggleCheck={props.toggleCheck} />
          <Button icon="pulse" variant="primary" onClick={() => props.execute(props.spec.primary)}>
            Test all connections
          </Button>
        </Card>
        <Card title="Trust boundary">
          <WorkspaceNotes notes={props.spec.notes} />
        </Card>
      </div>
    </div>
  );
}
function HelpLayout(props: WorkspaceLayoutProps) {
  return (
    <>
      <div className="v2-help-hero">
        <span>
          <Icon name="help" size={26} />
        </span>
        <div>
          <small>Exam Proctor Studio Help</small>
          <h2>What operational help do you need?</h2>
          <div className="input-wrap">
            <Icon name="search" size={14} />
            <input
              className="input"
              value={props.query}
              onChange={event => props.setQuery(event.target.value)}
              placeholder="Search guides, runbooks and support…"
            />
          </div>
        </div>
        <Button icon="message" variant="primary" onClick={() => props.execute(props.spec.primary)}>
          Contact support
        </Button>
      </div>
      <div className="v2-help-grid">
        {props.filtered.map((row, index) => (
          <button type="button" key={index} onClick={() => props.openRow(row)}>
            <span>
              <Icon
                name={
                  index % 5 === 0
                    ? "pulse"
                    : index % 5 === 1
                      ? "alert"
                      : index % 5 === 2
                        ? "accessibility"
                        : index % 5 === 3
                          ? "link"
                          : "shield"
                }
              />
            </span>
            <div>
              <small>{rowValue(row, "category")}</small>
              <b>{rowTitle(row)}</b>
              <p>{rowDetail(row)}</p>
            </div>
            <Icon name="arrow" size={14} />
          </button>
        ))}
      </div>
      <Card title="Emergency and live-session help">
        <WorkspaceNotes notes={props.spec.notes} />
      </Card>
    </>
  );
}

function renderWorkspaceModel(props: WorkspaceLayoutProps): React.ReactNode {
  switch (props.spec.model) {
    case "dashboard":
      return <DashboardLayout {...props} />;
    case "queue":
      return <QueueLayout {...props} />;
    case "cards":
      return <CardsLayout {...props} />;
    case "timeline":
    case "schedule":
      return <TimelineLayout {...props} />;
    case "board":
      return <BoardLayout {...props} />;
    case "health":
      return <HealthLayout {...props} />;
    case "matrix":
      return <MatrixLayout {...props} />;
    case "brief":
      return <BriefLayout {...props} />;
    case "calendar":
      return <CalendarLayout {...props} />;
    case "directory":
      return <DirectoryLayout {...props} />;
    case "roster":
      return <RosterLayout {...props} />;
    case "verification":
      return <VerificationLayout {...props} />;
    case "diagnostics":
      return <DiagnosticsLayout {...props} />;
    case "checklist":
      return <ChecklistLayout {...props} />;
    case "register":
      return <RegisterLayout {...props} />;
    case "grid":
      return <GridLayout {...props} />;
    case "panel":
      return <PanelLayout {...props} />;
    case "media":
      return <MediaLayout {...props} />;
    case "policy":
      return <PolicyLayout {...props} />;
    case "environment":
      return <EnvironmentLayout {...props} />;
    case "practical":
      return <PracticalLayout {...props} />;
    case "evidence":
      return <EvidenceLayout {...props} />;
    case "boundary":
      return <BoundaryLayout {...props} />;
    case "form":
      return <FormLayout {...props} />;
    case "statements":
      return <StatementsLayout {...props} />;
    case "logs":
      return <LogsLayout {...props} />;
    case "custody":
      return <CustodyLayout {...props} />;
    case "builder":
      return <BuilderLayout {...props} />;
    case "timers":
      return <TimersLayout {...props} />;
    case "support":
      return <SupportLayout {...props} />;
    case "analytics":
      return <AnalyticsLayout {...props} />;
    case "performance":
      return <PerformanceLayout {...props} />;
    case "review":
      return <ReviewLayout {...props} />;
    case "runbook":
      return <RunbookLayout {...props} />;
    case "certification":
      return <CertificationLayout {...props} />;
    case "simulation":
      return <SimulationLayout {...props} />;
    case "settings":
      return <SettingsLayout {...props} />;
    case "connections":
      return <ConnectionsLayout {...props} />;
    case "help":
      return <HelpLayout {...props} />;
    case "special":
      throw new Error(`Special route ${props.spec.id} must be rendered by its dedicated component.`);
    default:
      throw new Error(`Unsupported Exam Proctor workspace model: ${props.spec.model}`);
  }
}

export function DifferentiatedWorkspacePage({ cb, spec }: WorkspacePageProps) {
  const identityAccount = useIdentity();
  const isSecurity = spec.id === "security-sessions";
  const [securityBusy, setSecurityBusy] = useState(false);
  const securityLock = useRef(false);
  const revokeSessionsIntent = useRef<{ key: string; accountId: string; version: number } | null>(null);
  const initialRows = useMemo(() => (isSecurity ? [] : sourceRows(spec)), [spec, isSecurity]);
  const [rows, setRows] = useState<WorkspaceRow[]>(() =>
    isSecurity ? [] : safeRead(`chatoner-proctor:v2:rows:${spec.id}`, initialRows),
  );
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All states");
  const [activeTab, setActiveTab] = useState(spec.tabs[0] || "Operational view");
  const [checks, setChecks] = useState<Record<string, boolean>>(() =>
    safeRead(`chatoner-proctor:v2:checks:${spec.id}`, {}),
  );
  const [formValues, setFormValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(spec.fields.map(field => [field, ""])),
  );
  useEffect(() => {
    const next = isSecurity ? [] : sourceRows(spec);
    setRows(isSecurity ? [] : safeRead(`chatoner-proctor:v2:rows:${spec.id}`, next));
    setQuery("");
    setStatusFilter("All states");
    setActiveTab(spec.tabs[0] || "Operational view");
    setChecks(safeRead(`chatoner-proctor:v2:checks:${spec.id}`, {}));
    setFormValues(Object.fromEntries(spec.fields.map(field => [field, ""])));
  }, [spec, isSecurity]);
  useEffect(() => {
    if (!isSecurity) safeWrite(`chatoner-proctor:v2:rows:${spec.id}`, rows);
  }, [rows, spec.id, isSecurity]);
  useEffect(() => safeWrite(`chatoner-proctor:v2:checks:${spec.id}`, checks), [checks, spec.id]);
  const displayRows = useMemo<WorkspaceRow[]>(
    () =>
      isSecurity
        ? (identityAccount.security?.sessions || []).map(session => ({
            id: session.id,
            name:
              session.id === identityAccount.security?.currentSessionId ? "This device" : "Signed-in device",
            detail: session.user_agent || "Device not reported",
            last: new Date(session.last_seen_at).toLocaleString(),
            owner: identityAccount.security?.name || "",
            status: "Active",
          }))
        : rows,
    [isSecurity, identityAccount.security, rows],
  );
  const filtered = useMemo(
    () =>
      displayRows.filter(row => {
        const text = Object.values(row).join(" ").toLowerCase();
        const status = rowStatus(row, spec);
        return (
          text.includes(query.toLowerCase()) && (statusFilter === "All states" || status === statusFilter)
        );
      }),
    [displayRows, query, statusFilter, spec],
  );
  const openRow = (row: WorkspaceRow) => {
    if (spec.source === "sessions") cb.openModal("session-detail", row as unknown as Session);
    else if (spec.source === "candidates") cb.openModal("candidate-detail", row as unknown as Candidate);
    else if (spec.source === "incidents") cb.openModal("incident-detail", row as unknown as Incident);
    else if (spec.source === "evidence") cb.openModal("evidence-detail", row as unknown as EvidenceRecord);
    else if (spec.source === "accommodations")
      cb.openModal("accommodation-detail", row as unknown as Accommodation);
    else cb.openModal("secure-note", { title: rowTitle(row), text: rowDetail(row), row });
  };
  const advanceRow = (row: WorkspaceRow) => {
    const statusKey = spec.statusKey || "status";
    setRows(current =>
      current.map(candidate =>
        candidate === row
          ? {
              ...candidate,
              [statusKey]: /complete|resolved|acknowledged|healthy|verified/i.test(rowStatus(candidate, spec))
                ? "Reviewed"
                : "In progress",
            }
          : candidate,
      ),
    );
    cb.toast("Operational state updated", `${rowTitle(row)} remains attributed to ${rowOwner(row)}.`, "good");
  };
  const execute = (action: ProctorActionSpec, row?: WorkspaceRow) => {
    if (isSecurity && action.intent === spec.primary.intent) {
      const sessions = document.querySelector<HTMLDetailsElement>('[data-identity-section="sessions"]');
      if (sessions) {
        sessions.open = true;
        sessions.scrollIntoView({ block: "nearest" });
      }
      return;
    }
    const parts = action.intent.split(":");
    if (parts[0] === "navigate") {
      cb.navigate(parts[1], parts[2]);
      return;
    }
    if (parts[0] === "modal") {
      const kind = parts[1] as ModalKind;
      if (kind === "candidate-detail")
        cb.openModal(kind, (row || filtered[0] || APP_DATA.candidates[0]) as unknown as Candidate);
      else if (kind === "session-detail")
        cb.openModal(kind, (row || filtered[0] || APP_DATA.sessions[0]) as unknown as Session);
      else if (kind === "incident-detail")
        cb.openModal(kind, (row || filtered[0] || APP_DATA.incidents[0]) as unknown as Incident);
      else if (kind === "evidence-detail")
        cb.openModal(kind, (row || filtered[0] || APP_DATA.evidence[0]) as unknown as EvidenceRecord);
      else if (kind === "accommodation-detail")
        cb.openModal(kind, (row || filtered[0] || APP_DATA.accommodations[0]) as unknown as Accommodation);
      else cb.openModal(kind, { title: spec.title, text: spec.description, row });
      return;
    }
    if (parts[0] === "class") {
      const session = APP_DATA.sessions.find(item => item.platform.includes("Class")) || APP_DATA.sessions[0];
      cb.openClass(session, parts[1] || "proctor");
      return;
    }
    if (parts[0] === "lab") {
      const session = APP_DATA.sessions.find(item => item.platform.includes("Labs")) || APP_DATA.sessions[0];
      cb.openLab(session, parts[1] === "first" ? "dashboard" : parts[1] || "dashboard");
      return;
    }
    if (parts[0] === "export") {
      downloadText(
        `Chatoner-Exam-Proctor-${spec.id}-${Date.now()}.json`,
        JSON.stringify(
          {
            version: "2.0.0",
            route: spec.id,
            title: spec.title,
            purpose: spec.description,
            records: filtered,
            exportedAt: new Date().toISOString(),
            authority: "Exam Proctor operational data only; no academic outcome",
          },
          null,
          2,
        ),
        "application/json",
      );
      cb.toast(
        "Governed export prepared",
        `${filtered.length} visible records were added to the local prototype export.`,
        "good",
      );
      return;
    }
    if (action.intent === "action:advance" && filtered[0]) {
      advanceRow(filtered[0]);
      return;
    }
    if (action.intent === "action:test-connection" || action.intent === "action:test-device") {
      cb.toast(
        "Diagnostic completed",
        `${spec.title} checks passed in the demonstration environment.`,
        "good",
      );
      return;
    }
    if (action.intent === "action:start-simulation") {
      cb.toast(
        "Practice scenario started",
        "Select the human-led, policy-aligned response and record your rationale.",
        "info",
      );
      return;
    }
    if (action.intent === "action:save-settings") {
      cb.toast("Settings saved", `${spec.title} preferences were stored locally for this prototype.`, "good");
      return;
    }
    if (action.intent === "action:revoke-sessions") {
      if (securityLock.current) return;
      securityLock.current = true;
      setSecurityBusy(true);
      void (async () => {
        try {
          const security = identityAccount.security;
          if (!security?.accountId || !security.identityVersion)
            throw new Error("Refresh account security before signing out devices.");
          if (!revokeSessionsIntent.current || revokeSessionsIntent.current.accountId !== security.accountId)
            revokeSessionsIntent.current = {
              key: crypto.randomUUID(),
              accountId: security.accountId,
              version: security.identityVersion,
            };
          const intent = revokeSessionsIntent.current;
          const result = await identitySecurity.revokeOthers(intent.version, intent.key);
          revokeSessionsIntent.current = null;
          cb.toast(
            "Other sessions signed out",
            `${result.revokedSessions} other signed-in sessions revoked.`,
            "good",
          );
        } catch (e) {
          if (e instanceof AcademyApiError && e.code === "CONCURRENCY_CONFLICT")
            revokeSessionsIntent.current = null;
          cb.toast(
            "Unable to finish signing out devices",
            e instanceof Error ? e.message : "Refresh account security and retry.",
            "warn",
          );
        } finally {
          securityLock.current = false;
          setSecurityBusy(false);
        }
      })();
      return;
    }
    cb.openModal("secure-note", { title: action.label, text: spec.description, row });
  };
  const toggleCheck = (key: string) => setChecks(current => ({ ...current, [key]: !current[key] }));
  const saveForm = () => {
    const missing = spec.fields.filter(field => !formValues[field]?.trim());
    if (missing.length) {
      cb.toast(
        "Complete required operational fields",
        `Add ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? " and the remaining fields" : ""}.`,
        "warn",
      );
      return;
    }
    cb.toast(
      `${spec.title} saved`,
      "The demonstration record was stored with actor, purpose, time and authority context.",
      "good",
    );
    setFormValues(Object.fromEntries(spec.fields.map(field => [field, ""])));
  };
  const identityMetrics: ProctorMetricSpec[] = spec.metrics.map((metric, index) => ({
    ...metric,
    value:
      index === 0
        ? identityAccount.security
          ? String(displayRows.length)
          : "—"
        : index === 1
          ? identityAccount.security
            ? identityAccount.security.mfaEnabled
              ? "Enabled"
              : "Not enabled"
            : "—"
          : "—",
    trend: index < 2 ? "Your account" : "Not reported",
  }));
  const layoutProps: WorkspaceLayoutProps = {
    cb,
    spec: isSecurity ? { ...spec, metrics: identityMetrics } : spec,
    rows: displayRows,
    filtered,
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    openRow,
    advanceRow,
    execute,
    checks,
    toggleCheck,
    formValues,
    setFormValues,
    saveForm,
  };
  return (
    <WorkspaceRouteBoundary spec={spec}>
      <PageHero kicker={spec.areaLabel} title={spec.title} description={spec.description}>
        <Button icon={spec.primary.icon} variant="primary" onClick={() => execute(spec.primary, filtered[0])}>
          {spec.primary.label}
        </Button>
        {spec.secondary.map(action => (
          <Button
            disabled={
              securityBusy ||
              (isSecurity && action.intent === "action:revoke-sessions" && !identityAccount.security)
            }
            key={action.label}
            icon={action.icon}
            onClick={() => execute(action, filtered[0])}
          >
            {action.label}
          </Button>
        ))}
      </PageHero>
      <div className="integrity-banner">
        <span>
          <Icon name="shield" size={22} />
        </span>
        <div>
          <b>Exam Proctor operational authority</b>
          <p>
            Observe, support, document, preserve and escalate within the assigned session. Do not coach,
            grade, determine misconduct or issue academic outcomes.
          </p>
        </div>
        <Status>Human authority</Status>
      </div>
      <WorkspaceTabs tabs={spec.tabs} active={activeTab} setActive={setActiveTab} />
      <WorkspaceTabPanel title={spec.title} activeTab={activeTab} recordCount={filtered.length} />
      <WorkspaceMetrics metrics={layoutProps.spec.metrics} />
      {renderWorkspaceModel(layoutProps)}
    </WorkspaceRouteBoundary>
  );
}
